import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react'

export type Tab<T extends string> = {
  id: T
  label: string
  count?: number
}

export type TabsProps<T extends string> = {
  tabs: ReadonlyArray<Tab<T>>
  active: T
  onChange: (id: T) => void
  right?: ReactNode
}

export const Tabs = <T extends string>({ tabs, active, onChange, right }: TabsProps<T>) => {
  const stripRef = useRef<HTMLDivElement>(null)
  // Edge fades cue that the strip scrolls — each shown only when there
  // is more content toward that side. Four tabs don't fit a phone, so
  // without this the off-screen tab has no affordance.
  const [fade, setFade] = useState({ left: false, right: false })

  const updateFade = useCallback(() => {
    const el = stripRef.current
    if (!el) return
    const left = el.scrollLeft > 1
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
    // Bail when unchanged so the every-render measure below can't loop.
    setFade((prev) => (prev.left === left && prev.right === right ? prev : { left, right }))
  }, [])

  useEffect(() => {
    const el = stripRef.current
    if (!el) return
    el.addEventListener('scroll', updateFade, { passive: true })
    window.addEventListener('resize', updateFade)
    return () => {
      el.removeEventListener('scroll', updateFade)
      window.removeEventListener('resize', updateFade)
    }
  }, [updateFade])

  // Re-measure after every render — covers mount and tab/count changes.
  useEffect(() => {
    updateFade()
  })

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        borderBottom: '1px solid var(--line-soft)',
        padding: '0 14px',
        background: 'var(--paper)',
      }}
    >
      <div style={{ position: 'relative', flex: '1 1 auto', minWidth: 0 }}>
        <div
          ref={stripRef}
          role="tablist"
          style={{
            display: 'flex',
            gap: 0,
            overflowX: 'auto',
            scrollbarWidth: 'none',
          }}
        >
          {tabs.map((t) => {
            const isActive = t.id === active
            return (
              <button
                key={t.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                onClick={() => onChange(t.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '10px 12px 9px',
                  fontFamily: 'var(--font-display)',
                  fontSize: 13,
                  fontWeight: isActive ? 600 : 500,
                  color: isActive ? 'var(--ink)' : 'var(--ink-3)',
                  borderBottom: `2px solid ${isActive ? 'var(--terra)' : 'transparent'}`,
                  marginBottom: -1,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                {t.label}
                {t.count !== undefined && (
                  <span
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 10,
                      color: isActive ? 'var(--ink-2)' : 'var(--ink-3)',
                      background: 'var(--paper-2)',
                      borderRadius: 8,
                      padding: '1px 6px',
                      minWidth: 14,
                      textAlign: 'center',
                    }}
                  >
                    {t.count}
                  </span>
                )}
              </button>
            )
          })}
        </div>
        {fade.left && <ScrollFade side="left" />}
        {fade.right && <ScrollFade side="right" />}
      </div>
      {right}
    </div>
  )
}

const ScrollFade = ({ side }: { side: 'left' | 'right' }) => (
  <div
    aria-hidden
    style={{
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: side === 'left' ? 0 : 'auto',
      right: side === 'right' ? 0 : 'auto',
      width: 28,
      pointerEvents: 'none',
      background: `linear-gradient(to ${side}, transparent, var(--paper))`,
    }}
  />
)
